<div class="chart-editor content">
    <div class="content-header">
        <div class="title">
            <mat-icon class="header-icon">dashboard_customize</mat-icon>
            <span>{{ "editor.editor" | translate }} </span>
            <app-help [link]="helpLink"></app-help>
        </div>

        <div class="header-btns">
            @if(!scope && query && query.id) {
            <lib-button [matMenuTriggerFor]="embedMenu" color="outline" class="save-btn" icon="code"
                text="{{ 'editor.embed' | translate }}">
            </lib-button>

            <mat-menu #embedMenu="matMenu">
                <button mat-menu-item (click)="getEmbedUrl()">
                    <span>{{'editor.copy_embed_link' | translate}}</span>
                </button>

                <button (click)="getEmbedMacro()" mat-menu-item>
                    <span>{{'editor.copy_excel_macro' | translate}}</span>
                </button>
            </mat-menu>
            }

            @if (canManageTemplates && !editingTemplate) {
            <lib-button color="outline" class="save-btn template-btn" icon="bookmark_add"
                text="{{ 'catalog.save_as_template' | translate }}" (click)="saveAsTemplate()">
            </lib-button>
            }

            <lib-button color="primary" class="save-btn" icon="save"
                text="{{ (editingTemplate ? 'catalog.save_template' : 'form.save') | translate }}"
                (click)="saveChart()">
            </lib-button>

            <lib-button color="danger" class="cancel-btn" icon="close" (click)="closeDialog()"
                text="{{ 'form.close' | translate }}">
            </lib-button>
        </div>
    </div>

    <div class="content-wrapper">
        <div class="options">
            <mat-tab-group class="mat-tab-group widget-editor-tabs" animationDuration="220ms"
                (selectedTabChange)="onTabChanged($event)" [selectedIndex]="tabIndex" *ngIf="!showFieldList">
                <!-- Tab design -->
                <mat-tab label="{{ 'editor.design' | translate }}">
                    <div class="tab">
                        <!-- Optional settings, right after the title: greyed out with the reason when the widget does not allow them -->
                        <ng-template #optionCards>
                            <app-widget-option-card *ngIf="widget.type === 'chart' || widget.type === 'insight'"
                                class="forecast-card" icon="timeline" title="editor.forecast_card"
                                [enabled]="forecastEnabled" [disabled]="!canForecast()"
                                [reason]="forecastBlockedReason()" (toggled)="forecastEnabled = $event">
                                <ng-container *ngIf="forecastEnabled">
                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.forecast_horizon' | translate }}</mat-label>

                                        <input matInput type="number" min="1" [max]="forecastMaxHorizon()"
                                            [(ngModel)]="widget.query.forecast.horizonDays"
                                            (change)="onForecastHorizon()" />
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.forecast_method' | translate }}</mat-label>

                                        <mat-select [(ngModel)]="widget.query.forecast.method"
                                            (selectionChange)="updateWidget()">
                                            <mat-select-trigger>{{ 'editor.forecast_method_' + widget.query.forecast.method | translate }}</mat-select-trigger>
                                            <mat-option *ngFor="let method of forecastMethods" [value]="method"
                                                [disabled]="method === 'poisson' && !forecastCounts()" class="forecast-method">
                                                <span>{{ 'editor.forecast_method_' + method | translate }}</span>
                                                <small>{{ 'editor.forecast_method_' + method + '_hint' | translate }}</small>
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.forecast_confidence' | translate }}</mat-label>

                                        <mat-select [(ngModel)]="widget.query.forecast.confidence"
                                            (selectionChange)="updateWidget()">
                                            <mat-option *ngFor="let confidence of forecastConfidences"
                                                [value]="confidence">
                                                {{ confidence * 100 }} %
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>
                                </ng-container>
                            </app-widget-option-card>

                            <app-widget-option-card *ngIf="widget.type === 'block'" class="trend-card"
                                icon="trending_up" title="editor.compare" toggleLabel="editor.compare_previous"
                                [enabled]="compareEnabled" [disabled]="!hasDateFilter()"
                                reason="editor.compare_needs_date" (toggled)="compareEnabled = $event">
                                <mat-checkbox [ngModel]="trendHigherIsWorse"
                                    (ngModelChange)="trendHigherIsWorse = $event">
                                    {{ 'editor.compare_higher_is_worse' | translate }}
                                </mat-checkbox>
                            </app-widget-option-card>

                            <!-- Running total of a dated chart, and the target it heads for -->
                            <app-widget-option-card *ngIf="widget.type === 'chart' && widget.graphType !== 'barDistribution'"
                                class="cumulative-card" icon="stacked_line_chart" title="editor.cumulative_card"
                                [enabled]="!!widget.design.cumulative?.enabled" [disabled]="!hasDateAxis()"
                                reason="editor.forecast_needs_date_axis"
                                (toggled)="cumulative.enabled = $event; updateVisuals()">
                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ 'editor.cumulative_target' | translate }}</mat-label>
                                    <input matInput type="number" min="0" [(ngModel)]="cumulative.target"
                                        (change)="updateVisuals()" />
                                </mat-form-field>
                            </app-widget-option-card>

                            <!-- Points typed by hand, drawn with the data (a target, a reference) -->
                            <app-widget-option-card *ngIf="widget.type === 'chart'" class="manual-series-card"
                                icon="edit_note" title="editor.manual_series_card" [enabled]="!!widget.dataset?.enable"
                                (toggled)="manualSeries.enable = $event; updateWidget()">
                                <p class="card-hint">{{ 'editor.manual_series_hint' | translate }}</p>

                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ "editor.dataset" | translate }}</mat-label>
                                    <input matInput type="text" [maxLength]="256" [(ngModel)]="manualSeries.label"
                                        (change)="updateWidget()" />
                                </mat-form-field>

                                <div class="manual-point" *ngFor="let point of manualSeries.data; let index = index">
                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ "editor.x" | translate }}</mat-label>
                                        <input matInput type="text" [maxLength]="256" [(ngModel)]="point.x"
                                            (change)="updateWidget()" />
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ "editor.y" | translate }}</mat-label>
                                        <input matInput type="text" [maxLength]="200" [(ngModel)]="point.y"
                                            (change)="updateWidget()" />
                                    </mat-form-field>

                                    <lib-button color="none" textColor="muted" icon="close" size="small"
                                        (click)="deleteDatasetData(index)"></lib-button>
                                </div>

                                <lib-button color="primary" size="small" icon="add" (click)="addDatasetData()"
                                    text="{{ 'editor.manual_series_add' | translate }}"></lib-button>
                            </app-widget-option-card>

                            <!-- Landing: done since the start of the period + its forecast to the end -->
                            <app-widget-option-card *ngIf="widget.type === 'block'" class="landing-card" icon="flag"
                                title="editor.landing_card" [enabled]="!!widget.design.landing?.enabled"
                                [disabled]="!forecastAdditive()" reason="editor.landing_needs_sum"
                                (toggled)="toggleLanding($event)">
                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ 'editor.landing_period' | translate }}</mat-label>
                                    <mat-select [ngModel]="widget.design.landing?.period ?? 'month'"
                                        (ngModelChange)="widget.design.landing.period = $event; updateWidget()">
                                        <mat-option *ngFor="let period of landingPeriods" [value]="period">
                                            {{ 'editor.landing_period_' + period | translate }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </app-widget-option-card>

                            <ng-container *ngIf="widget.type === 'insight'">
                                <app-widget-option-card class="insight-figure-card" icon="pin"
                                    title="editor.insight_figure" [enabled]="!!widget.design.insight?.figure?.enabled"
                                    (toggled)="toggleInsightBlock('figure', $event)">
                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.insight_source' | translate }}</mat-label>
                                        <mat-select [ngModel]="insightBlock('figure').source ?? 'meanDays'"
                                            (ngModelChange)="insightBlock('figure').source = $event; updateVisuals()">
                                            <mat-option *ngFor="let source of insightSources" [value]="source">
                                                {{ 'editor.insight_source_' + source | translate }}
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.title' | translate }}</mat-label>
                                        <input matInput [(ngModel)]="insightBlock('figure').label"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>
                                </app-widget-option-card>

                                <app-widget-option-card class="insight-probabilities-card" icon="percent"
                                    title="editor.insight_probabilities"
                                    [enabled]="!!widget.design.insight?.probabilities?.enabled"
                                    [disabled]="widget.query?.forecast?.method !== 'poisson'"
                                    reason="editor.insight_needs_poisson"
                                    (toggled)="toggleInsightBlock('probabilities', $event)">
                                </app-widget-option-card>

                                <app-widget-option-card *ngIf="scope" class="insight-ranking-card" icon="leaderboard"
                                    title="editor.insight_ranking" [enabled]="!!widget.design.insight?.ranking?.enabled"
                                    (toggled)="toggleInsightBlock('ranking', $event)">
                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.insight_top' | translate }}</mat-label>
                                        <input matInput type="number" min="1" [placeholder]="'' + defaultRankingTop"
                                            [(ngModel)]="insightBlock('ranking').top" (change)="updateVisuals()" />
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.column_warning' | translate }}</mat-label>
                                        <input matInput type="number" [(ngModel)]="insightBlock('ranking').warning"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.column_critical' | translate }}</mat-label>
                                        <input matInput type="number" [(ngModel)]="insightBlock('ranking').critical"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>

                                    <mat-checkbox [ngModel]="insightBlock('ranking').higherIsWorse !== false"
                                        (ngModelChange)="insightBlock('ranking').higherIsWorse = $event; updateVisuals()">
                                        {{ 'editor.column_higher_is_worse' | translate }}
                                    </mat-checkbox>
                                </app-widget-option-card>

                                <app-widget-option-card class="insight-note-card" icon="notes"
                                    title="editor.insight_note" [enabled]="!!widget.design.insight?.note?.enabled"
                                    (toggled)="toggleInsightBlock('note', $event)">
                                    <mat-form-field appearance="outline" subscriptSizing="dynamic"
                                        class="insight-note-field">
                                        <mat-label>{{ 'editor.insight_note_preset' | translate }}</mat-label>
                                        <mat-select (selectionChange)="applyNotePreset($event.value)">
                                            <mat-option *ngFor="let preset of notePresets" [value]="preset">
                                                {{ 'editor.insight_note_preset_' + preset | translate }}
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic"
                                        class="insight-note-field">
                                        <mat-label>{{ 'editor.insight_note_text' | translate }}</mat-label>
                                        <textarea matInput rows="3" [(ngModel)]="insightBlock('note').text"
                                            (change)="updateVisuals()"></textarea>
                                    </mat-form-field>

                                    <!-- The variables, replaced by their value on the widget -->
                                    <div class="insight-note-variables">
                                        <button type="button" class="note-variable" *ngFor="let variable of noteVariables"
                                            [matTooltip]="'editor.insight_variable_' + variable | translate"
                                            (click)="addNoteVariable(variable)">{{ '{' + variable + '}' }}</button>
                                    </div>
                                </app-widget-option-card>
                            </ng-container>

                            <app-widget-option-card *ngIf="widget.type === 'projectMap'" class="project-map-card"
                                icon="public" title="editor.project_map_card" [switchable]="false">
                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ 'editor.column_warning' | translate }}</mat-label>
                                    <input matInput type="number" [(ngModel)]="projectMap.warning"
                                        (change)="updateVisuals()" />
                                </mat-form-field>

                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ 'editor.column_critical' | translate }}</mat-label>
                                    <input matInput type="number" [(ngModel)]="projectMap.critical"
                                        (change)="updateVisuals()" />
                                </mat-form-field>

                                <mat-checkbox [ngModel]="projectMap.higherIsWorse !== false"
                                    (ngModelChange)="projectMap.higherIsWorse = $event; updateVisuals()">
                                    {{ 'editor.column_higher_is_worse' | translate }}
                                </mat-checkbox>

                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ 'editor.project_map_max_radius' | translate }}</mat-label>
                                    <input matInput type="number" min="4" [placeholder]="'' + defaultMaxRadius"
                                        [(ngModel)]="projectMap.maxRadius" (change)="updateVisuals()" />
                                </mat-form-field>
                            </app-widget-option-card>

                            <app-widget-option-card *ngIf="widget.type === 'projectMap'" class="project-map-legend-card"
                                icon="legend_toggle" title="editor.project_map_legend" [enabled]="!!projectMap.legend"
                                (toggled)="projectMap.legend = $event; updateVisuals()">
                            </app-widget-option-card>

                            <app-widget-option-card *ngIf="widget.type === 'heatmap'" class="heatmap-card"
                                icon="grid_on" title="editor.heatmap_card" [switchable]="false"
                                [disabled]="heatmapBlockedReason() !== null" [reason]="heatmapBlockedReason()">
                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ 'editor.heatmap_scale' | translate }}</mat-label>

                                    <mat-select [ngModel]="heatmap.scale ?? 'quantile'"
                                        (ngModelChange)="heatmap.scale = $event; updateVisuals()">
                                        <mat-option value="quantile">{{ 'editor.heatmap_scale_quantile' | translate
                                            }}</mat-option>
                                        <mat-option value="fixed">{{ 'editor.heatmap_scale_fixed' | translate
                                            }}</mat-option>
                                    </mat-select>
                                </mat-form-field>

                                <ng-container *ngIf="heatmap.scale === 'fixed'">
                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.heatmap_medium_from' | translate }}</mat-label>
                                        <input matInput type="number" [(ngModel)]="heatmap.medium"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.heatmap_high_from' | translate }}</mat-label>
                                        <input matInput type="number" [(ngModel)]="heatmap.high"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>
                                </ng-container>

                                <lib-color-picker label="editor.heatmap_color" [value]="heatmap.color || '#1F77B4'"
                                    (valueChange)="heatmap.color = $event; updateVisuals()">
                                </lib-color-picker>
                            </app-widget-option-card>

                            <app-widget-option-card *ngIf="widget.graphType === 'barDistribution'"
                                class="distribution-card" icon="percent" title="editor.distribution_card"
                                [switchable]="false">
                                <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                    <mat-label>{{ 'editor.bar_sort' | translate }}</mat-label>
                                    <mat-select [ngModel]="distribution.sort ?? 'value'"
                                        (ngModelChange)="distribution.sort = $event; updateVisuals()">
                                        <mat-option value="value">{{ 'editor.sort_value_desc' | translate
                                            }}</mat-option>
                                        <mat-option value="category">{{ 'editor.sort_category' | translate
                                            }}</mat-option>
                                    </mat-select>
                                </mat-form-field>

                                <mat-checkbox [ngModel]="distribution.showPercent !== false"
                                    (ngModelChange)="distribution.showPercent = $event; updateVisuals()">
                                    {{ 'editor.distribution_show_percent' | translate }}
                                </mat-checkbox>
                            </app-widget-option-card>
                        </ng-template>

                        <ng-container *ngIf="!hasTitleForm()" [ngTemplateOutlet]="optionCards"></ng-container>

                        <ng-container *ngFor="let form of chartDesignForm">
                            <app-widget-form [form]="form" [field]="'design'" (onActions)="actionForm($event)"
                                (onUpdate)="onFormUpdate($event)"></app-widget-form>

                            <ng-container *ngIf="isTitleForm(form)" [ngTemplateOutlet]="optionCards"></ng-container>
                        </ng-container>

                        <!-- Table: how each column is shown, and the total row -->
                        <app-widget-option-card *ngIf="widget.type === 'table' && tableColumns().length"
                            class="table-columns-card" icon="view_column" title="editor.table_columns"
                            [enabled]="tableOptionsOn()" (toggled)="setTableOptions($event)">
                            <div class="table-column" *ngFor="let column of tableColumns()">
                                <div class="table-column-head">
                                    <span class="table-column-name" [title]="column">{{ column }}</span>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.column_format' | translate }}</mat-label>
                                        <mat-select [ngModel]="columnDesign(column).format"
                                            (ngModelChange)="setColumnFormat(column, $event)">
                                            <mat-option *ngFor="let format of columnFormats" [value]="format">
                                                {{ 'editor.column_format_' + format | translate }}
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>
                                </div>

                                <div class="table-column-options" *ngIf="columnDesign(column).format === 'chip'">
                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.column_warning' | translate }}</mat-label>
                                        <input matInput type="number"
                                            [(ngModel)]="widget.design.columns[column].warning"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.column_critical' | translate }}</mat-label>
                                        <input matInput type="number"
                                            [(ngModel)]="widget.design.columns[column].critical"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>

                                    <mat-checkbox class="span-2"
                                        [(ngModel)]="widget.design.columns[column].higherIsWorse"
                                        (change)="updateVisuals()">
                                        {{ 'editor.column_higher_is_worse' | translate }}
                                    </mat-checkbox>
                                </div>

                                <div class="table-column-options" *ngIf="columnDesign(column).format === 'progress'">
                                    <mat-form-field class="span-2" appearance="outline" subscriptSizing="dynamic">
                                        <mat-label>{{ 'editor.column_max' | translate }}</mat-label>
                                        <input matInput type="number" [(ngModel)]="widget.design.columns[column].max"
                                            (change)="updateVisuals()" />
                                    </mat-form-field>
                                </div>
                            </div>

                            <mat-checkbox [ngModel]="!!widget.design.total"
                                (ngModelChange)="widget.design.total = $event; updateVisuals()">
                                {{ 'editor.table_total' | translate }}
                            </mat-checkbox>
                        </app-widget-option-card>
                    </div>
                </mat-tab>

                <!-- Tab aggregation -->
                <mat-tab label="{{ 'editor.aggregation' | translate }}">
                    <div class="tab">
                        <!-- Synthesis: one value per project of the tenant, or their total. -->
                        <div class="by-project-toggle" *ngIf="scope && canCompareProjects()">
                            <mat-icon>splitscreen</mat-icon>
                            <mat-slide-toggle [checked]="!!widget.query.byProject"
                                (change)="widget.query.byProject = $event.checked; updateWidget()">
                                {{ 'synthesis.split_by_project' | translate }}
                            </mat-slide-toggle>
                        </div>

                        <ng-container *ngFor="let opt of graphOption">
                            <ng-container *ngIf="opt.room">
                                <div class="axis-title">{{ opt.name }}</div>

                                <ng-container *ngIf="chartQuery[opt.input]">
                                    <app-widget-form *ngFor="let form of chartQuery[opt.input]" [form]="form"
                                        [field]="opt.input" (onUpdate)="onFormUpdate($event)">
                                    </app-widget-form>
                                </ng-container>

                                <lib-button color="primary" class="add-btn" (click)="addField(opt.input)" size="medium"
                                    *ngIf="!chartQuery[opt.input] || chartQuery[opt.input].length < opt.room" icon="add"
                                    text="{{ 'editor.add_field' | translate }}">
                                </lib-button>
                            </ng-container>
                        </ng-container>
                    </div>
                </mat-tab>

                <!-- Tab filter -->
                <mat-tab label="{{ 'editor.filter' | translate }}">
                    <div class="tab">
                        <div *ngIf="filterOption">
                            <ng-container *ngFor="let opt of filterOption">
                                <ng-container *ngIf="opt.room">
                                    <div class="axis-title">{{ opt.name }}</div>

                                    <ng-container *ngIf="chartQuery[opt.input]">
                                        <app-widget-form *ngFor="let form of chartQuery[opt.input]" [form]="form"
                                            [field]="opt.input" [category]="widget.category || ''" [tenantId]="scope?.tenantId"
                                            (onUpdate)="onFormUpdate($event)"></app-widget-form>
                                    </ng-container>

                                    <lib-button color="primary" class="add-btn" (click)="addField(opt.input)"
                                        *ngIf="!chartQuery[opt.input] || chartQuery[opt.input].length < 5"
                                        text="{{ 'editor.add_filter' | translate }}" icon="add">
                                    </lib-button>
                                </ng-container>
                            </ng-container>
                        </div>

                        <div *ngIf="!filterOption">
                            <ng-container *ngIf="chartQuery.filters">
                                <!-- Each filter, then what it keeps in a sentence (and of today, for a date) -->
                                <ng-container *ngFor="let form of chartQuery.filters">
                                    <app-widget-form [category]="widget.category || ''" [form]="form" [tenantId]="scope?.tenantId"
                                        [field]="'filters'" (onUpdate)="onFormUpdate($event)"></app-widget-form>
                                    <app-filter-summary
                                        [filter]="widget.query.filters?.[form.name]"></app-filter-summary>
                                </ng-container>
                            </ng-container>

                            <div class="add-btn">
                                <lib-button color="primary" class="add-btn" (click)="addFilter()"
                                    *ngIf="!chartQuery.filters || chartQuery.filters.length < 5"
                                    text="{{ 'editor.add_filter' | translate }}" icon="add">
                                </lib-button>
                            </div>
                        </div>
                    </div>
                </mat-tab>
            </mat-tab-group>

            <!-- The data fields: by source and measure type, searchable; one source per widget -->
            <div class="field-container" *ngIf="showFieldList">
                <app-field-picker [locked]="lockedSource()" [tenantId]="scope?.tenantId" [cache]="fieldCache"
                    (picked)="pickField($event)" (closed)="closeFieldList()"></app-field-picker>
            </div>
        </div>

        <div class="preview">
            <div class="preview-header">
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="header-title-field">
                    <mat-label>{{ "editor.title" | translate }}</mat-label>
                    <input matInput type="text" [maxLength]="256" [(ngModel)]="widget.title" #titleInput />
                </mat-form-field>

                <!-- Where the widget lives (synthesis: its tenant and theme) and what it draws -->
                <div class="header-fields">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" *ngIf="!scope && isPersonal">
                        <mat-label>{{ "editor.projects" | translate }}</mat-label>
                        <mat-select [(ngModel)]="widget.projects" multiple (selectionChange)="onProjectSelected()">
                            <mat-option [value]="opt.id" *ngFor="let opt of projects">{{ opt.name }}</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" subscriptSizing="dynamic" *ngIf="!scope">
                        <mat-label>{{ "dashboard.title" | translate }}</mat-label>
                        <mat-select [(ngModel)]="dashboardId">
                            <mat-option [value]="opt.id" *ngFor="let opt of dashboards">{{ opt.title }}</mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>{{ "editor.graph_type" | translate }}</mat-label>
                        <mat-select [(ngModel)]="widget.graphType" (selectionChange)="onGraphTypeChange(); updateWidget()">
                            <mat-select-trigger>
                                <div class="selected-graph-type" *ngIf="getSelectedGraphType()">
                                    <mat-icon>{{ getSelectedGraphType().icon }}</mat-icon>
                                    <span>{{ getSelectedGraphType().name | translate }}</span>
                                </div>
                            </mat-select-trigger>

                            <mat-option [value]="opt.value" *ngFor="let opt of graphTypes">
                                <div class="selected-graph-type">
                                    <mat-icon>{{ opt.icon }}</mat-icon>
                                    <span>{{ opt.name | translate }}</span>
                                </div>
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>{{ "editor.limit" | translate }}</mat-label>
                        <input matInput type="number" placeholder="5000" [min]="0" [(ngModel)]="widgetQueryLimit"
                            (change)="onLimitChanged()" />
                    </mat-form-field>
                </div>

                <div class="header-toggles" *ngIf="!scope || compareDisplay">
                    <mat-slide-toggle *ngIf="!scope" [(ngModel)]="widget.databaseSeries"
                        (change)="onDatabaseSeriesChanged()">
                        {{ "editor.database_series" | translate }}
                    </mat-slide-toggle>

                    <mat-slide-toggle *ngIf="compareDisplay" [(ngModel)]="widget.compareMode" (change)="onCompareMode()">
                        {{ "editor.compareMode" | translate }}
                    </mat-slide-toggle>
                </div>
            </div>

            <div class="widget-wrapper">
                <!-- The preview right after the first paint of the editor: the opening is not one long freeze.
                     A message if its code cannot load (an app updated since the page was opened). -->
                @defer (on immediate) {
                <app-widget-host [widget]="widget" (query)="onQueryUpdated($event)" [inputs]="{
                    width: widget.width || 600, height: widget.height || 400, refreshSignal: refreshSignal,
                    visualSignal: visualSignal, signalSingled: widget.signalSingled, projects: projects,
                    tenantId: scope?.tenantId }">
                </app-widget-host>
                } @placeholder {
                <app-spinner></app-spinner>
                } @error {
                <p class="preview-error">{{ 'editor.preview_error' | translate }}</p>
                }
            </div>

        </div>
    </div>
</div>